const LUPZ_ZOOMABLE_IMAGE_SELECTOR = [
  '.lupz-popup-main-image img',
  '.lupz-popup-mobile-gallery__slide img',
  '.lupz-popup-mobile-image img',
  '.lupz-collection-gallery__item-image img',
].join(', ');

const LUPZ_ZOOMABLE_HOST_SELECTOR = [
  '.lupz-popup-main-image',
  '.lupz-popup-mobile-image',
  '.lupz-popup-mobile-gallery-host',
  '.lupz-collection-gallery__item-image',
].join(', ');

const LUPZ_POPUP_PANEL_SELECTOR = '.lupz-series-popup-panel, .lupz-popup-panel';
const LUPZ_MIN_SCALE = 1;
const LUPZ_MAX_SCALE = 3;

const toLargeImageUrl = (url) => {
  if (!url) {
    return url;
  }

  try {
    const parsed = new URL(url, window.location.origin);

    if (parsed.searchParams.has('width')) {
      parsed.searchParams.set('width', '2400');
    } else if (/_\d+x(\d+)?\./.test(parsed.pathname)) {
      parsed.pathname = parsed.pathname.replace(/_\d+x(\d+)?\./, '.');
    } else {
      parsed.searchParams.set('width', '2400');
    }

    return parsed.toString();
  } catch (error) {
    return url;
  }
};

class LupzImageZoom {
  #root;
  #stage;
  #image;
  #prevButton;
  #nextButton;
  #images = [];
  #index = 0;
  #scale = 1;
  #translateX = 0;
  #translateY = 0;
  #isDragging = false;
  #dragStartX = 0;
  #dragStartY = 0;
  #dragOriginX = 0;
  #dragOriginY = 0;
  #pointers = new Map();
  #pinchStartDistance = 0;
  #pinchStartScale = 1;
  #touchMoved = false;
  #swipeStartX = 0;
  #swipeStartY = 0;
  #isSwipeTracking = false;
  #hint;
  #counter;

  constructor() {
    this.#root = document.createElement('div');
    this.#root.id = 'LupzImageZoom';
    this.#root.className = 'lupz-image-zoom';
    this.#root.setAttribute('aria-hidden', 'true');
    this.#root.innerHTML = `
      <button type="button" class="lupz-image-zoom__backdrop" data-lupz-zoom-close aria-label="Close image"></button>
      <button type="button" class="lupz-image-zoom__close" data-lupz-zoom-close aria-label="Close image">×</button>
      <button type="button" class="lupz-image-zoom__nav lupz-image-zoom__nav--prev" data-lupz-zoom-prev aria-label="Previous image"></button>
      <button type="button" class="lupz-image-zoom__nav lupz-image-zoom__nav--next" data-lupz-zoom-next aria-label="Next image"></button>
      <p class="lupz-image-zoom__counter" aria-live="polite"></p>
      <div class="lupz-image-zoom__stage">
        <img class="lupz-image-zoom__image" alt="">
      </div>
      <p class="lupz-image-zoom__hint"></p>
    `;

    document.body.appendChild(this.#root);

    this.#stage = this.#root.querySelector('.lupz-image-zoom__stage');
    this.#image = this.#root.querySelector('.lupz-image-zoom__image');
    this.#prevButton = this.#root.querySelector('[data-lupz-zoom-prev]');
    this.#nextButton = this.#root.querySelector('[data-lupz-zoom-next]');
    this.#hint = this.#root.querySelector('.lupz-image-zoom__hint');
    this.#counter = this.#root.querySelector('.lupz-image-zoom__counter');

    this.#bindEvents();
    this.#decorateZoomTargets();
    this.#bindExpandButtons();

    const observer = new MutationObserver(() => {
      this.#decorateZoomTargets();
      this.#bindExpandButtons();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  #resolveImageFromButton(expandButton) {
    const host = expandButton.closest(LUPZ_ZOOMABLE_HOST_SELECTOR);

    if (host) {
      const hostImage = host.querySelector('img:not(.lupz-zoomable-expand__icon)');

      if (hostImage) {
        return hostImage;
      }
    }

    const previous = expandButton.previousElementSibling;

    if (previous) {
      if (previous.tagName === 'IMG') {
        return previous;
      }

      const previousImage = previous.querySelector('img');

      if (previousImage) {
        return previousImage;
      }
    }

    const panel = expandButton.closest(LUPZ_POPUP_PANEL_SELECTOR);

    if (!panel) {
      return null;
    }

    const galleryHost = expandButton.closest('.lupz-popup-mobile-gallery-host');

    if (galleryHost) {
      const mobileScroller = galleryHost.querySelector('.lupz-popup-mobile-image-scroll');

      if (mobileScroller) {
        const slideWidth = mobileScroller.clientWidth || 1;
        const activeIndex = Math.round(mobileScroller.scrollLeft / slideWidth);
        const mobileImages = mobileScroller.querySelectorAll('.lupz-popup-mobile-image img');

        if (mobileImages[activeIndex]) {
          return mobileImages[activeIndex];
        }
      }
    }

    const mainImage = panel.querySelector('[data-lupz-popup-preview]') || panel.querySelector('.lupz-popup-preview-img, .lupz-main-popup-img');

    if (mainImage) {
      return mainImage;
    }

    const mobileScroller = panel.querySelector('.lupz-popup-mobile-image-scroll');

    if (mobileScroller) {
      const slideWidth = mobileScroller.clientWidth || 1;
      const activeIndex = Math.round(mobileScroller.scrollLeft / slideWidth);
      const mobileImages = mobileScroller.querySelectorAll('.lupz-popup-mobile-image img');

      if (mobileImages[activeIndex]) {
        return mobileImages[activeIndex];
      }
    }

    const galleryTrack = panel.querySelector('.lupz-popup-mobile-gallery__track');

    if (galleryTrack) {
      const slideWidth = galleryTrack.clientWidth || 1;
      const activeIndex = Math.round(galleryTrack.scrollLeft / slideWidth);
      const galleryImages = galleryTrack.querySelectorAll('img');

      if (galleryImages[activeIndex]) {
        return galleryImages[activeIndex];
      }
    }

    return panel.querySelector(LUPZ_ZOOMABLE_IMAGE_SELECTOR);
  }

  #bindExpandButton(expandButton) {
    if (expandButton.dataset.lupzZoomBound === 'true') {
      return;
    }

    expandButton.dataset.lupzZoomBound = 'true';

    expandButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const image = this.#resolveImageFromButton(expandButton);

      if (image) {
        this.#openFromImage(image);
      }
    });
  }

  #bindExpandButtons() {
    for (const expandButton of document.querySelectorAll('[data-lupz-zoom-open]')) {
      this.#bindExpandButton(expandButton);
    }
  }

  #bindEvents() {
    document.addEventListener('click', (event) => {
      if (event.target.closest('.lupz-popup-thumbs')) {
        return;
      }

      if (event.target.closest('[data-lupz-zoom-open]')) {
        return;
      }

      const target = event.target.closest(LUPZ_ZOOMABLE_IMAGE_SELECTOR);

      if (!target || target.closest('.lupz-popup-thumbs')) {
        return;
      }

      if (this.#touchMoved) {
        this.#touchMoved = false;
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.#openFromImage(target);
    }, true);

    this.#root.addEventListener('click', (event) => {
      if (event.target.closest('[data-lupz-zoom-close]')) {
        event.stopPropagation();
        this.#close();
      }
    });

    this.#prevButton.addEventListener('click', (event) => {
      event.stopPropagation();
      this.#step(-1);
    });

    this.#nextButton.addEventListener('click', (event) => {
      event.stopPropagation();
      this.#step(1);
    });

    document.addEventListener('keydown', (event) => {
      if (!this.#root.classList.contains('is-open')) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.#close();
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.#step(-1);
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.#step(1);
      }
    }, true);

    this.#stage.addEventListener('wheel', (event) => {
      if (!this.#root.classList.contains('is-open')) {
        return;
      }

      event.preventDefault();
      const delta = event.deltaY < 0 ? 0.12 : -0.12;
      this.#setScale(this.#scale + delta, event.clientX, event.clientY);
    }, { passive: false });

    this.#stage.addEventListener('pointerdown', (event) => {
      if (!this.#root.classList.contains('is-open')) {
        return;
      }

      this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.#stage.setPointerCapture(event.pointerId);

      if (this.#pointers.size === 1) {
        this.#swipeStartX = event.clientX;
        this.#swipeStartY = event.clientY;
        this.#isSwipeTracking = this.#scale === 1 && this.#images.length > 1;
      }

      if (this.#pointers.size === 1 && this.#scale > 1) {
        this.#isDragging = true;
        this.#dragStartX = event.clientX;
        this.#dragStartY = event.clientY;
        this.#dragOriginX = this.#translateX;
        this.#dragOriginY = this.#translateY;
      }

      if (this.#pointers.size === 2) {
        this.#isDragging = false;
        this.#isSwipeTracking = false;
        this.#pinchStartDistance = this.#getPointerDistance();
        this.#pinchStartScale = this.#scale;
      }
    });

    this.#stage.addEventListener('pointermove', (event) => {
      if (!this.#pointers.has(event.pointerId)) {
        return;
      }

      this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.#pointers.size === 2) {
        const distance = this.#getPointerDistance();

        if (this.#pinchStartDistance > 0) {
          const midpoint = this.#getPointerMidpoint();
          const nextScale = this.#pinchStartScale * (distance / this.#pinchStartDistance);
          this.#setScale(nextScale, midpoint.x, midpoint.y);
        }

        return;
      }

      if (this.#isDragging) {
        const deltaX = event.clientX - this.#dragStartX;
        const deltaY = event.clientY - this.#dragStartY;

        if (Math.abs(deltaX) > 6 || Math.abs(deltaY) > 6) {
          this.#touchMoved = true;
        }

        this.#translateX = this.#dragOriginX + deltaX;
        this.#translateY = this.#dragOriginY + deltaY;
        this.#applyTransform();
      }
    });

    const endPointer = (event) => {
      const wasSinglePointer = this.#pointers.size === 1;

      if (
        wasSinglePointer &&
        this.#scale === 1 &&
        this.#isSwipeTracking &&
        !this.#isDragging &&
        this.#images.length > 1
      ) {
        const deltaX = event.clientX - this.#swipeStartX;
        const deltaY = event.clientY - this.#swipeStartY;

        if (Math.abs(deltaX) >= 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
          this.#step(deltaX > 0 ? -1 : 1);
          this.#touchMoved = true;
        }
      }

      this.#pointers.delete(event.pointerId);

      if (this.#pointers.size < 2) {
        this.#pinchStartDistance = 0;
      }

      if (this.#pointers.size === 0) {
        this.#isDragging = false;
        this.#isSwipeTracking = false;
      }
    };

    this.#stage.addEventListener('pointerup', endPointer);
    this.#stage.addEventListener('pointercancel', endPointer);

    this.#stage.addEventListener('dblclick', (event) => {
      if (!this.#root.classList.contains('is-open')) {
        return;
      }

      if (this.#scale > 1) {
        this.#resetTransform();
        return;
      }

      this.#setScale(2, event.clientX, event.clientY);
    });
  }

  #shouldAutoInjectExpand(host) {
    if (host.querySelector('[data-lupz-zoom-open]') || !host.querySelector('img')) {
      return false;
    }

    if (host.closest('.lupz-product-popup-root')) {
      return false;
    }

    if (host.classList.contains('lupz-popup-mobile-image') && host.closest('.lupz-popup-mobile-gallery-host')) {
      return false;
    }

    if (host.classList.contains('lupz-popup-mobile-gallery-host')) {
      return false;
    }

    const panel = host.closest(LUPZ_POPUP_PANEL_SELECTOR);

    if (host.classList.contains('lupz-popup-main-image') && panel?.querySelector('.lupz-popup-mobile-gallery-host')) {
      return false;
    }

    return true;
  }

  #cleanupDuplicateExpandButtons() {
    for (const button of document.querySelectorAll('.lupz-popup-mobile-gallery-host .lupz-popup-mobile-image [data-lupz-zoom-open]')) {
      button.remove();
    }

    for (const host of document.querySelectorAll('.lupz-popup-mobile-gallery-host')) {
      const hostButtons = host.querySelectorAll(':scope > [data-lupz-zoom-open]');

      for (let index = 1; index < hostButtons.length; index += 1) {
        hostButtons[index].remove();
      }
    }
  }

  #decorateZoomTargets() {
    this.#cleanupDuplicateExpandButtons();

    for (const image of document.querySelectorAll(LUPZ_ZOOMABLE_IMAGE_SELECTOR)) {
      image.classList.add('lupz-zoomable');
    }

    for (const host of document.querySelectorAll(LUPZ_ZOOMABLE_HOST_SELECTOR)) {
      if (!this.#shouldAutoInjectExpand(host)) {
        continue;
      }

      host.classList.add('lupz-zoomable-host');

      const expandButton = document.createElement('button');
      expandButton.type = 'button';
      expandButton.className = 'lupz-zoomable-expand';
      expandButton.setAttribute('data-lupz-zoom-open', '');
      expandButton.setAttribute('aria-label', 'Expand image');
      expandButton.innerHTML = '<svg class="lupz-zoomable-expand__icon" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><circle cx="6.75" cy="6.75" r="4.25" fill="none" stroke="currentColor" stroke-width="1.4"></circle><path d="M10 10L14 14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"></path><path d="M6.75 4.5V9" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"></path><path d="M4.5 6.75H9" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"></path></svg>';
      host.appendChild(expandButton);
    }
  }

  #collectImages(sourceImage) {
    const panel = sourceImage.closest(LUPZ_POPUP_PANEL_SELECTOR);
    const gallery = sourceImage.closest('.lupz-collection-gallery');
    const images = [];

    if (panel) {
      const thumbButtons = panel.querySelectorAll('.lupz-popup-thumbs button[data-full]');

      if (thumbButtons.length) {
        for (const button of thumbButtons) {
          const fullImage = button.getAttribute('data-full');

          if (!fullImage) {
            continue;
          }

          images.push({
            src: toLargeImageUrl(fullImage),
            alt: button.querySelector('img')?.alt || '',
          });
        }
      } else {
        for (const slideImage of panel.querySelectorAll('.lupz-popup-mobile-gallery__slide img, .lupz-popup-mobile-image img')) {
          const src = toLargeImageUrl(slideImage.currentSrc || slideImage.src);

          if (!images.some((item) => item.src === src)) {
            images.push({
              src,
              alt: slideImage.alt || '',
            });
          }
        }
      }
    }

    if (gallery) {
      for (const galleryImage of gallery.querySelectorAll('.lupz-collection-gallery__item-image img')) {
        images.push({
          src: toLargeImageUrl(galleryImage.currentSrc || galleryImage.src),
          alt: galleryImage.alt || '',
        });
      }
    }

    if (!images.length) {
      images.push({
        src: toLargeImageUrl(sourceImage.currentSrc || sourceImage.src),
        alt: sourceImage.alt || '',
      });
    }

    return images;
  }

  #getZoomSurface(sourceImage) {
    const host = sourceImage.closest(LUPZ_ZOOMABLE_HOST_SELECTOR);

    if (host) {
      const hostBackground = window.getComputedStyle(host).backgroundColor;

      if (hostBackground && hostBackground !== 'rgba(0, 0, 0, 0)' && hostBackground !== 'transparent') {
        return hostBackground;
      }
    }

    return '#f6f4f3';
  }

  #applyZoomSurface(sourceImage) {
    const surface = this.#getZoomSurface(sourceImage);
    this.#root.style.setProperty('--lupz-zoom-surface', surface);
    this.#stage.style.background = surface;
  }

  #updateChrome() {
    const hasMultiple = this.#images.length > 1;

    this.#prevButton.classList.toggle('is-hidden', !hasMultiple);
    this.#nextButton.classList.toggle('is-hidden', !hasMultiple);

    if (hasMultiple) {
      this.#counter.textContent = `${this.#index + 1} / ${this.#images.length}`;
      this.#counter.hidden = false;

      if (window.matchMedia('(max-width: 768px)').matches) {
        this.#hint.textContent = 'Swipe for more photos · Pinch to zoom';
      } else {
        this.#hint.textContent = 'Use arrows or swipe · Scroll to zoom';
      }
    } else {
      this.#counter.hidden = true;
      this.#hint.textContent = 'Pinch or scroll to zoom · Drag to move';
    }
  }

  #openFromImage(sourceImage) {
    this.#images = this.#collectImages(sourceImage);
    this.#index = this.#resolveStartIndex(sourceImage);
    this.#applyZoomSurface(sourceImage);
    this.#showCurrentImage();
    this.#root.classList.add('is-open');
    this.#root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('lupz-image-zoom-open');
  }

  #resolveStartIndex(sourceImage) {
    const panel = sourceImage.closest(LUPZ_POPUP_PANEL_SELECTOR);
    const sourceSrc = toLargeImageUrl(sourceImage.currentSrc || sourceImage.src);

    if (panel) {
      const activeThumb = panel.querySelector('.lupz-popup-thumbs button.is-active[data-full]');

      if (activeThumb) {
        const activeSrc = toLargeImageUrl(activeThumb.getAttribute('data-full'));
        const activeIndex = this.#images.findIndex((item) => item.src === activeSrc);

        if (activeIndex >= 0) {
          return activeIndex;
        }
      }
    }

    const foundIndex = this.#images.findIndex((item) => item.src === sourceSrc);
    return foundIndex >= 0 ? foundIndex : 0;
  }

  #showCurrentImage() {
    const current = this.#images[this.#index];

    if (!current) {
      return;
    }

    this.#resetTransform();
    this.#image.src = current.src;
    this.#image.alt = current.alt;
    this.#updateChrome();
  }

  #step(delta) {
    if (this.#images.length <= 1) {
      return;
    }

    this.#index = (this.#index + delta + this.#images.length) % this.#images.length;
    this.#showCurrentImage();
  }

  #close() {
    this.#root.classList.remove('is-open');
    this.#root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('lupz-image-zoom-open');
    this.#resetTransform();
    this.#image.removeAttribute('src');
  }

  #resetTransform() {
    this.#scale = 1;
    this.#translateX = 0;
    this.#translateY = 0;
    this.#applyTransform();
  }

  #setScale(nextScale, clientX, clientY) {
    const clampedScale = Math.min(LUPZ_MAX_SCALE, Math.max(LUPZ_MIN_SCALE, nextScale));

    if (clampedScale === 1) {
      this.#resetTransform();
      return;
    }

    const stageRect = this.#stage.getBoundingClientRect();
    const originX = clientX - stageRect.left - stageRect.width / 2;
    const originY = clientY - stageRect.top - stageRect.height / 2;
    const scaleRatio = clampedScale / this.#scale;

    this.#translateX = originX - (originX - this.#translateX) * scaleRatio;
    this.#translateY = originY - (originY - this.#translateY) * scaleRatio;
    this.#scale = clampedScale;
    this.#applyTransform();
  }

  #applyTransform() {
    this.#image.style.transform = `translate(calc(-50% + ${this.#translateX}px), calc(-50% + ${this.#translateY}px)) scale(${this.#scale})`;
  }

  #getPointerDistance() {
    const points = [...this.#pointers.values()];

    if (points.length < 2) {
      return 0;
    }

    const dx = points[0].x - points[1].x;
    const dy = points[0].y - points[1].y;
    return Math.hypot(dx, dy);
  }

  #getPointerMidpoint() {
    const points = [...this.#pointers.values()];
    return {
      x: (points[0].x + points[1].x) / 2,
      y: (points[0].y + points[1].y) / 2,
    };
  }
}

const initLupzImageZoom = () => {
  if (window.LupzImageZoom) {
    return window.LupzImageZoom;
  }

  window.LupzImageZoom = new LupzImageZoom();
  return window.LupzImageZoom;
};

initLupzImageZoom();
